import { z } from 'zod';

const readingListStorySchema = z.object({
  id: z.union([z.string(), z.number()]),
  type: z.enum(['Story', 'Audiobook']).optional(),
  cover: z.string().nullish(),
  name: z.string().optional(),
  slug: z.string().optional(),
  cover_url: z.string().optional(),
  original_cover: z.string().nullable().optional(),
  promo_carousel_cover: z.string().nullable().optional(),
  original_story_id: z.number().optional(),
  is_adult: z.boolean().optional(),
  is_available_for_silver: z.boolean().optional(),
  is_available_for_unlimited: z.boolean().optional(),
  is_dev: z.boolean().optional(),
  is_disabled_for_new_readers: z.boolean().optional(),
  partner_content: z.boolean().optional(),
  subscribers_only: z.boolean().optional(),
  points_only: z.boolean().optional(),
  user_progress: z.number().optional(),
  author: z.string().optional(),
  author_data: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  language_codename: z.string().optional(),
  next_release: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  contract_type: z.string().nullable().optional(),
  url_slug: z.string().nullish(),
  publisher: z.string().nullable().optional(),
  split_series_id: z.string().nullable().optional(),
  series_name: z.string().nullable().optional(),
  position: z.number().nullable().optional(),
  books_in_series: z.number().nullable().optional(),
  recommendation_scores: z
    .object({
      story_score: z.number().nullable().optional(),
      metric_score: z.number().nullable().optional(),
      intrest_score: z.number().nullable().optional(),
    })
    .optional(),
  bookmark_save_count: z.number().optional(),
  chapters_started_count: z.number().optional(),
  comment_count: z.number().optional(),
  shared_episode_count: z.number().optional(),
  story_start_count: z.number().optional(),
  genres: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  sub_genres: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  subjects: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  trigger_warnings: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  aggregate_genres: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  aggregate_content_flags: z
    .array(
      z.object({
        slug: z.string(),
        title: z.string(),
      }),
    )
    .optional(),
  test_covers: z
    .array(
      z.object({
        key: z.string(),
        cover: z.string(),
        chance: z.number(),
        title: z.string().nullable(),
        original_cover: z.string().nullable(),
      }),
    )
    .optional(),
  test_cover: z
    .object({
      key: z.string().nullable(),
      cover: z.string().nullable(),
      chance: z.number(),
      title: z.string().nullable(),
      original_cover: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  summary: z
    .object({
      key: z.string().optional(),
      chance: z.number().optional(),
      content: z.string().optional(),
    })
    .nullable()
    .optional(),
});

const readingListTagSchema = z.object({
  tag_id: z.number(),
  tag_slug: z.string(),
  tag_name: z.string(),
  score: z.number(),
  emoji: z.string(),
  percentage: z.number(),
  cltv_score: z.number(),
});

const readingListSchema = z.object({
  id: z.number(),
  title: z.string().optional(),
  userId: z.string(),
  type: z.enum(['Story', 'Audiobook', 'regular']).optional(),
  kind: z.enum(['my_library', 'regular']).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  language: z.string().optional(),
  visible: z.boolean().optional(),
  followersCount: z.number().optional(),
  storiesCount: z.number().optional(),
  stories: z.array(readingListStorySchema).optional(),
  tags: z.array(readingListTagSchema).nullable().optional(),
  isIndexable: z.boolean().optional(),
  isSeriesList: z.boolean().optional(),
  isAuthorList: z.boolean().optional(),
});

export const readingListSearchResponse = z.object({
  reading_lists: z.array(readingListSchema),
  pagination: z.object({
    current_page: z.number(),
    current_page_items: z.number(),
    items_per_page: z.number(),
  }),
});

export const readingListDiscoveryResponse = z.object({
  readingLists: z.array(readingListSchema),
  pagination: z.object({
    current_page: z.number(),
    current_page_items: z.number(),
    items_per_page: z.number(),
  }),
});

export const readingListFollowedResponse = z.object({
  readingLists: z.array(readingListSchema),
  pagination: z.object({
    current_page: z.number(),
    current_page_items: z.number(),
    items_per_page: z.number(),
  }),
});

export const readingListDetailResponse = z.object({
  reading_list: readingListSchema,
});

export const readingListStoriesResponse = z.object({
  stories: z.array(readingListStorySchema),
  pagination: z.object({
    current_page: z.number(),
    current_page_items: z.number(),
    items_per_page: z.number(),
    is_last_page: z.boolean().optional(),
    total_items: z.number().optional(),
  }),
});

export type ReadingListStory = z.infer<typeof readingListStorySchema>;
export type ReadingList = z.infer<typeof readingListSchema>;
export type ReadingListSearchResponse = z.infer<typeof readingListSearchResponse>;
export type ReadingListDiscoveryResponse = z.infer<typeof readingListDiscoveryResponse>;
export type ReadingListFollowedResponse = z.infer<typeof readingListFollowedResponse>;
export type ReadingListDetailResponse = z.infer<typeof readingListDetailResponse>;
export type ReadingListStoriesResponse = z.infer<typeof readingListStoriesResponse>;
